import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rangesForDifficulty(d: Difficulty): { aMin: number; aMax: number; bMin: number; bMax: number } {
  if (d === 1) return { aMin: 10, aMax: 19, bMin: 10, bMax: 19 };
  if (d === 2) return { aMin: 10, aMax: 29, bMin: 10, bMax: 19 };
  return { aMin: 10, aMax: 49, bMin: 10, bMax: 49 };
}

export function generateMul2x2(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);
  const { aMin, aMax, bMin, bMax } = rangesForDifficulty(difficulty);

  const a = Math.floor(rand() * (aMax - aMin + 1)) + aMin;
  const b = Math.floor(rand() * (bMax - bMin + 1)) + bMin;
  const correct = a * b;

  // Distractors model typical 兩位×兩位 mistakes
  const distractors = new Set<number>();

  // 1. Forgot to shift the tens-place row left (adds without shifting)
  //    Standard algorithm: units row = a × (b%10), tens row = a × floor(b/10)
  //    Mistake: adds them without × 10 shift for tens row
  const unitsRow = a * (b % 10);
  const tensRowNoShift = a * Math.floor(b / 10); // forgot ×10
  const noShift = unitsRow + tensRowNoShift;
  if (noShift !== correct && noShift > 0) distractors.add(noShift);

  // 2. Only multiplied units digits of both numbers
  const unitsOnly = (a % 10) * (b % 10);
  if (unitsOnly !== correct && unitsOnly > 0) distractors.add(unitsOnly);

  // 3. Off-by-10 carry error (common rounding mistake)
  const offByCarry = correct + 10;
  if (offByCarry !== correct) distractors.add(offByCarry);

  // Fill any missing distractor with random offsets
  const offsets = [20, -20, 30, -30, 50, -50, 100, -100];
  let oi = 0;
  while (distractors.size < 3 && oi < offsets.length) {
    const candidate = correct + offsets[oi];
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
    oi++;
  }
  while (distractors.size < 3) {
    const offset = Math.floor(rand() * 60) - 30;
    const candidate = correct + (offset === 0 ? 15 : offset);
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  // Deterministic shuffle via seed
  const shuffled = [...optionList].sort((x, y) => {
    const px = mulberry32(seed + x)();
    const py = mulberry32(seed + y)();
    return px - py;
  });

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `${a}×${b%10}=${unitsRow},${a}×${Math.floor(b/10)}=${a*Math.floor(b/10)} (記得左移一位) → ${unitsRow}+${a*Math.floor(b/10)*10}=${correct}`,
    kp: 'math.mul.2x2',
  };
}
